import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import styles from './DashboardPage.module.css';

const isValidStripeCheckoutUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com';
  } catch {
    return false;
  }
};

const isValidStripeOnboardingUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'connect.stripe.com';
  } catch {
    return false;
  }
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.events)) return payload.events;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const calcEventMetrics = (event) => {
  // サーバー側で集計した metrics を使用（dashboardRelations の hosted events に付与）
  if (event?.metrics) {
    return {
      sold:     toNumber(event.metrics.sold),
      revenue:  toNumber(event.metrics.revenue),
      capacity: toNumber(event.metrics.capacity),
      progress: toNumber(event.metrics.progress),
    };
  }
  // performing / providing イベントは統計なし
  return { sold: 0, revenue: 0, capacity: 0, progress: 0 };
};

const formatYen = (value) => `¥${toNumber(value).toLocaleString('ja-JP')}`;

const isCancelledStatus = (status) => {
  const value = String(status || 'none');
  return [
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ].includes(value);
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sellerStatus, setSellerStatus] = useState(user?.seller_status || 'none');
  const [sellerActionLoading, setSellerActionLoading] = useState(false);
  const [showOnboardingIntro, setShowOnboardingIntro] = useState(false);
  const [cancelTargetEvent, setCancelTargetEvent] = useState(null);
  const [cancelEstimate, setCancelEstimate] = useState(null);
  const [cancelEstimateLoading, setCancelEstimateLoading] = useState(false);
  const [cancelEstimateError, setCancelEstimateError] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [abortingEventId, setAbortingEventId] = useState(null);
  const [abortTargetEvent, setAbortTargetEvent] = useState(null);
  const [draftEvents, setDraftEvents] = useState([]);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [deleteTargetDraft, setDeleteTargetDraft] = useState(null);

  const isSeller = ['organizer', 'artist', 'venue'].includes(user?.role);

  const loadSellerConnectStatus = async () => {
    if (!user?.id || !isSeller) return;

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/me/seller/connect-status`);
      setSellerStatus(res.data?.seller_status || 'none');
    } catch (e) {
      setSellerStatus(user?.seller_status || 'none');
    }
  };

  const loadDashboard = async () => {
    if (!user?.id || !isSeller) return;

    setLoading(true);
    setError('');

    try {
      await loadSellerConnectStatus();

      const relationsRes = await axios.get(`${process.env.REACT_APP_API_URL}/me/dashboard/relations`);

      const hosted = normalizeCollection(relationsRes.data?.hosted_events).map((event) => ({
        ...event,
        relationType: 'hosted',
      }));
      const performing = normalizeCollection(relationsRes.data?.performing_events).map((event) => ({
        ...event,
        relationType: 'performing',
      }));
      const providing = normalizeCollection(relationsRes.data?.providing_events).map((event) => ({
        ...event,
        relationType: 'providing',
      }));

      const merged = [...hosted, ...performing, ...providing].map((event) => ({
        ...event,
        isPast: (() => {
          const endRef = event?.end_date || event?.date;
          if (!endRef) return false;
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          return endRef < todayStr; // 終了日が今日より前のみ終了扱い（当日は開催中）
        })(),
        ...calcEventMetrics(event),
      }));

      setEvents(merged);

      const pendingTargets = hosted
        .filter((event) => !isCancelledStatus(event?.cancellation_status));
      const pendingResults = await Promise.all(
        pendingTargets.map(async (event) => {
          const eventPathId = event?.slug || event?.id;
          try {
            const res = await axios.get(
              `${process.env.REACT_APP_API_URL}/organizer/events/${eventPathId}/memories/pending`
            );
            const items = normalizeCollection(res.data);
            const pendingCount = items.filter((img) => String(img?.status || '') !== 'approved').length;
            return {
              eventId: event.id,
              eventPathId,
              eventName: event.name,
              total: pendingCount,
            };
          } catch (e) {
            return { eventId: event.id, eventPathId, eventName: event.name, total: 0 };
          }
        })
      );

      const total = pendingResults.reduce((sum, x) => sum + x.total, 0);
      const byEvent = pendingResults
        .filter((x) => x.total > 0)
        .sort((a, b) => b.total - a.total);

      setPendingEvents(byEvent);
      setPendingTotal(total);

      // 下書きイベント
      setDraftEvents(Array.isArray(relationsRes.data?.draft_events) ? relationsRes.data.draft_events : []);
    } catch (e) {
      setError('ダッシュボードの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSellerStatus(user?.seller_status || 'none');
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.seller_status]);

  useEffect(() => {
    if (sellerStatus !== 'none') {
      setShowOnboardingIntro(false);
    }
  }, [sellerStatus]);

  const ownedEvents = useMemo(
    () => events.filter((event) => event.relationType === 'hosted'),
    [events]
  );

  const performingEvents = useMemo(
    () => events.filter((event) => event.relationType === 'performing'),
    [events]
  );

  const providingEvents = useMemo(
    () => events.filter((event) => event.relationType === 'providing'),
    [events]
  );

  const relationSections = useMemo(() => {
    const sections = [];
    if (ownedEvents.length) {
      sections.push({ key: 'hosted', title: '主催イベント', events: ownedEvents, relationType: 'hosted' });
    }
    if (performingEvents.length) {
      sections.push({ key: 'performing', title: '出演イベント', events: performingEvents, relationType: 'performing' });
    }
    if (providingEvents.length) {
      sections.push({ key: 'providing', title: '会場提供イベント', events: providingEvents, relationType: 'providing' });
    }
    return sections;
  }, [ownedEvents, performingEvents, providingEvents]);

  const canViewSales = ownedEvents.length > 0;
  const canCreateEvent = sellerStatus === 'active';
  const canOpenOnboardingModal = sellerStatus === 'none' || sellerStatus === 'onboarding' || sellerStatus === 'restricted';

  const upcomingHostedCount = useMemo(
    () => ownedEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [ownedEvents]
  );
  const upcomingPerformingCount = useMemo(
    () => performingEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [performingEvents]
  );
  const upcomingProvidingCount = useMemo(
    () => providingEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [providingEvents]
  );

  const totalRevenue = useMemo(
    () => ownedEvents.reduce((sum, event) => sum + toNumber(event.revenue), 0),
    [ownedEvents]
  );

  const salesRows = useMemo(
    () =>
      ownedEvents.map((event) => ({
        id: event.id,
        name: event.name,
        sales: event.revenue,
        sold: event.sold,
      })),
    [ownedEvents]
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCancelEvent = async (event) => {
    setCancelTargetEvent(event);
    setCancelEstimate(null);
    setCancelEstimateError('');
    setCancelEstimateLoading(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/${event.id}/cancel-estimate`);
      setCancelEstimate(res.data || null);
    } catch (e) {
      setCancelEstimateError('中止見積りの取得に失敗しました。');
    } finally {
      setCancelEstimateLoading(false);
    }
  };

  const closeCancelModal = () => {
    if (cancelSubmitting) return;
    setCancelTargetEvent(null);
    setCancelEstimate(null);
    setCancelEstimateError('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetEvent?.id || cancelSubmitting) return;
    setCancelSubmitting(true);
    setCancelEstimateError('');

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${cancelTargetEvent.id}/cancel`);
      const payload = res.data || {};

      if (payload.requires_payment && payload.checkout_url) {
        if (!isValidStripeCheckoutUrl(payload.checkout_url)) {
          window.alert('無効なリダイレクト先です。');
          return;
        }
        window.location.href = payload.checkout_url;
        return;
      }

      window.alert('イベント中止を開始しました。返金処理を実行します。');
      setCancelTargetEvent(null);
      setCancelEstimate(null);
      setCancelEstimateError('');
      await loadDashboard();
    } catch (e) {
      setCancelEstimateError(e?.response?.data?.message || '中止処理の開始に失敗しました。');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleResumeCancelPayment = async (event) => {
    if (!event?.id) return;

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${event.id}/cancel/resume`);
      const payload = res.data || {};

      if (payload.checkout_url) {
        if (!isValidStripeCheckoutUrl(payload.checkout_url)) {
          window.alert('無効なリダイレクト先です。');
          return;
        }
        window.location.href = payload.checkout_url;
        return;
      }

      window.alert('支払い再開リンクの発行に失敗しました。');
    } catch (e) {
      window.alert(e?.response?.data?.message || '支払い再開に失敗しました。');
    }
  };

  const handleAbortCancellation = (event) => {
    if (!event?.id || abortingEventId) return;
    setAbortTargetEvent(event);
  };

  const closeAbortModal = () => {
    if (abortingEventId) return;
    setAbortTargetEvent(null);
  };

  const handleConfirmAbortCancellation = async () => {
    if (!abortTargetEvent?.id || abortingEventId) return;

    setAbortingEventId(abortTargetEvent.id);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/events/${abortTargetEvent.id}/cancel/abort`);
      window.alert('中止フローを取り消しました。');
      setAbortTargetEvent(null);
      await loadDashboard();
    } catch (e) {
      window.alert(e?.response?.data?.message || '中止フローの取り消しに失敗しました。');
    } finally {
      setAbortingEventId(null);
    }
  };

  const handleDeleteDraft = (draft) => {
    setDeleteTargetDraft(draft);
  };

  const closeDeleteDraftModal = () => {
    if (deletingDraftId) return;
    setDeleteTargetDraft(null);
  };

  const handleConfirmDeleteDraft = async () => {
    if (!deleteTargetDraft?.id || deletingDraftId) return;
    setDeletingDraftId(deleteTargetDraft.id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/me/events/${deleteTargetDraft.id}/draft`);
      setDraftEvents((prev) => prev.filter((d) => d.id !== deleteTargetDraft.id));
      setDeleteTargetDraft(null);
    } catch (e) {
      window.alert(e?.response?.data?.message || '下書きの削除に失敗しました。');
    } finally {
      setDeletingDraftId(null);
    }
  };

  const handleStartOnboarding = async () => {
    setSellerActionLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/me/seller/connect/start`, {
        accept_terms: true,
      });
      if (res.data?.onboarding_url) {
        if (!isValidStripeOnboardingUrl(res.data.onboarding_url)) {
          window.alert('無効なリダイレクト先です。');
          return;
        }
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      window.alert('Stripeオンボーディングの開始に失敗しました。');
    } finally {
      setSellerActionLoading(false);
    }
  };

  const handleResumeOnboarding = async () => {
    setSellerActionLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/me/seller/connect/resume`);
      if (res.data?.onboarding_url) {
        if (!isValidStripeOnboardingUrl(res.data.onboarding_url)) {
          window.alert('無効なリダイレクト先です。');
          return;
        }
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      window.alert('オンボーディング再開に失敗しました。');
    } finally {
      setSellerActionLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.container}>読み込み中...</div>;
  }

  if (error) {
    return <div className={styles.container}>{error}</div>;
  }

  const renderEventCard = (event, relationType) => {
    const eventPathId = event?.slug || event?.id;
    const isHost = relationType === 'hosted';
    const canManageEvent = isHost && !event.isPast;
    const isCanceledEvent = isCancelledStatus(event?.cancellation_status);
    const isCancellationPendingPayment = (event?.cancellation_status || '') === 'cancellation_pending_payment';
    const canStartCancellation = canManageEvent && !isCanceledEvent && !isCancellationPendingPayment;
    const canResumeCancellationPayment = isHost && isCancellationPendingPayment;
    const canViewEventSales = isHost;
    const showSalesOnlyActions = isCanceledEvent;
    const flyerUrl = event?.flyers?.[0]?.url || '';

    return (
      <article key={`${relationType}-${event.id}`} className={styles.eventCard}>
        <img className={styles.thumb} src={flyerUrl} alt="flyer" />
        <div>
          <h3 className={styles.eventName}>
            {event.name}
            {relationType !== 'hosted' && (
              <span className={styles.relationTag}>
                {relationType === 'performing' ? '出演' : '会場提供'}
              </span>
            )}
          </h3>
          <p className={styles.eventDate}>{String(event.date || '').slice(0, 10)}</p>
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} style={{ width: `${event.progress}%` }} />
          </div>
          <div className={styles.metricsRow}>
            {isHost ? (
              <>
                <span>販売数: {event.sold}{event.capacity > 0 ? ` / ${event.capacity}` : ''}</span>
                <span>売上: {formatYen(event.revenue)}</span>
              </>
            ) : (
              <span className={styles.muted}>売上情報は主催者のみ表示</span>
            )}
          </div>
          <div className={styles.actions}>
            {showSalesOnlyActions ? (
              canViewEventSales ? (
                <Link className={`${styles.btn} ${styles.salesBtn}`} to={`/events/${eventPathId}/sales`}>
                  <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                  売上
                </Link>
              ) : null
            ) : (
              <>
                {canStartCancellation && (
                  <>
                    <Link className={`${styles.btn} ${styles.editBtn}`} to={`/events/${eventPathId}/edit`}>
                      <img className={styles.actionIcon} src="/icons/edit.svg" alt="" aria-hidden="true" />
                      編集
                    </Link>
                    <button className={`${styles.btn} ${styles.btnDanger} ${styles.cancelBtn}`} onClick={() => handleCancelEvent(event)}>
                      <img className={styles.actionIcon} src="/icons/cancelled.svg" alt="" aria-hidden="true" />
                      中止
                    </button>
                  </>
                )}
                {canResumeCancellationPayment && (
                  <>
                    <button className={`${styles.btn} ${styles.resumeBtn}`} onClick={() => handleResumeCancelPayment(event)}>
                      <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                      中止を再開
                    </button>
                    <button
                      className={`${styles.btn} ${styles.abortBtn}`}
                      onClick={() => handleAbortCancellation(event)}
                      disabled={abortingEventId === event.id}
                    >
                      {abortingEventId === event.id ? '取消中...' : '中止をキャンセル'}
                    </button>
                  </>
                )}
                {canViewEventSales && (
                  <Link className={`${styles.btn} ${styles.salesBtn}`} to={`/events/${eventPathId}/sales`}>
                    <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                    売上
                  </Link>
                )}
                <Link className={styles.btn} to={`/events/${eventPathId}`}>詳細</Link>
              </>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Link to="/" className={styles.backLink}>← サイトへ戻る</Link>

          <div className={styles.accountWrap}>
            <button
              type="button"
              className={styles.accountButton}
              onClick={() => setMenuOpen((prev) => !prev)}
            >≡</button>
            {menuOpen && (
              <div className={styles.accountMenu}>
                <Link className={styles.menuItem} to={`/profile/${user?.slug || 'my'}`} onClick={() => setMenuOpen(false)}>
                  プロフィール
                </Link>
                <Link
                  className={styles.menuItem}
                  to="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                >
                  ログアウト
                </Link>
              </div>
            )}
          </div>
        </header>

        {canOpenOnboardingModal && showOnboardingIntro && (
          <div
            className={styles.onboardingModalBackdrop}
            onClick={() => {
              if (!sellerActionLoading) setShowOnboardingIntro(false);
            }}
          >
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="本人確認について">
              <h3 className={styles.onboardingIntroTitle}>本人確認について</h3>
              <p className={styles.onboardingIntroLead}>
                IRIE TIXでは安全な決済のため、Stripeによる本人確認が必要です。
              </p>
              <div className={styles.onboardingProfileNotice} style={{marginBottom: '1em', color: '#b91c1c', fontWeight: 'bold'}}>
                ※ Stripe本人確認前に、<br />
                <span style={{textDecoration: 'underline'}}>プロフィール画像・説明・SNS等の情報を必ず入力してください。</span><br />
                未入力の場合、審査に通らない場合があります。
              </div>
              <ul className={styles.onboardingIntroList}>
                <li><img src="/icons/clock.svg" alt="時計" style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />所要時間：約5分</li>
                <li><img src="/icons/camera.svg" alt="カメラ" style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />本人確認書類（運転免許証など）が必要です</li>
                <li><img src="/icons/ginko.svg" alt="銀行" style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />振込口座情報を入力します</li>
              </ul>
              <p className={styles.onboardingIntroNote}>
                <Link className={styles.onboardingIntroLink} to="/seller-kyc-guide">
                  入力内容に迷われた方はこちらをご覧ください
                </Link>
              </p>
              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={sellerStatus === 'none' ? handleStartOnboarding : handleResumeOnboarding}
                  disabled={sellerActionLoading}
                >
                  {sellerActionLoading ? '処理中...' : sellerStatus === 'none' ? '本人確認を開始する' : '本人確認を再開する'}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={() => setShowOnboardingIntro(false)}
                  disabled={sellerActionLoading}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}

        {cancelTargetEvent && (
          <div className={styles.onboardingModalBackdrop} onClick={closeCancelModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="イベント中止確認">
              <h3 className={styles.onboardingIntroTitle}>イベント中止の確認</h3>
              <p className={styles.onboardingIntroLead}>「{cancelTargetEvent.name}」を中止します。</p>

              {cancelEstimateLoading ? (
                <p className={styles.muted}>見積りを取得中です...</p>
              ) : (
                <>
                  <ul className={styles.cancelEstimateList}>
                    <li>返金対象予約数：{toNumber(cancelEstimate?.refund_target_reservation_count || cancelEstimate?.refund_target_reservation_count === 0 ? cancelEstimate?.refund_target_reservation_count : cancelEstimate?.refund_target_count)}件</li>
                    <li>返金対象投げ銭数：{toNumber(cancelEstimate?.refund_target_donation_count)}件</li>
                    <li>返金総額（チケット）: {formatYen(cancelEstimate?.refund_total_amount_reservations)}</li>
                    <li>返金総額（投げ銭）: {formatYen(cancelEstimate?.refund_total_amount_donations)}</li>
                    <li>Stripe手数料見込み（チケット）: {formatYen(cancelEstimate?.estimated_stripe_fee_reservations)}</li>
                    <li>Stripe手数料見込み（投げ銭分）: {formatYen(cancelEstimate?.donation_processor_fee_total)}</li>
                    <li>チャージバック手数料見込み：{formatYen(cancelEstimate?.estimated_chargeback_fee_total)}</li>
                    <li>手数料合計: {formatYen(cancelEstimate?.organizer_burden_estimate)}</li>
                    <li><strong>中止手数料（3.6%＋¥40含む目安）: {formatYen(cancelEstimate?.estimated_cancel_fee_gross)}</strong></li>
                  </ul>
                  <p className={styles.cancelWarningText}>中止確定後は元に戻せません。主催者手数料の支払い後に返金処理を開始します。</p>
                </>
              )}

              {cancelEstimateError && <p className={styles.connectError}>{cancelEstimateError}</p>}

              <div className={styles.onboardingIntroActions}>
                <button type="button" className={styles.connectPrimaryBtn} onClick={handleConfirmCancel} disabled={cancelEstimateLoading || cancelSubmitting || !!cancelEstimateError || !cancelEstimate}>
                  {cancelSubmitting ? '処理中...' : 'Stripe手数料を支払って中止を確定する'}
                </button>
                <button type="button" className={styles.onboardingCancelBtn} onClick={closeCancelModal} disabled={cancelSubmitting}>
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}

        {abortTargetEvent && (
          <div className={styles.onboardingModalBackdrop} onClick={closeAbortModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="中止フロー取り消し確認">
              <h3 className={styles.onboardingIntroTitle}>中止フローの取り消し</h3>
              <p className={styles.onboardingIntroLead}>「{abortTargetEvent.name}」の中止フローを取り消します。</p>
              <p className={styles.cancelWarningText}>中止手数料の支払い待ちを取り消し、イベント状態を通常に戻します。</p>

              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={handleConfirmAbortCancellation}
                  disabled={abortingEventId === abortTargetEvent.id}
                >
                  {abortingEventId === abortTargetEvent.id ? '取消中...' : '中止をキャンセルする'}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={closeAbortModal}
                  disabled={abortingEventId === abortTargetEvent.id}
                >
                  戻る
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteTargetDraft && (
          <div className={styles.onboardingModalBackdrop} onClick={closeDeleteDraftModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="下書き削除確認">
              <h3 className={styles.onboardingIntroTitle}>下書きの削除</h3>
              <p className={styles.onboardingIntroLead}>「{deleteTargetDraft.name || '名称未設定'}」を削除します。</p>
              <p className={styles.cancelWarningText}>削除すると元に戻せません。</p>
              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={`${styles.connectPrimaryBtn} ${styles.btnDanger}`}
                  onClick={handleConfirmDeleteDraft}
                  disabled={deletingDraftId === deleteTargetDraft.id}
                >
                  {deletingDraftId === deleteTargetDraft.id ? '削除中...' : '削除する'}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={closeDeleteDraftModal}
                  disabled={deletingDraftId === deleteTargetDraft.id}
                >
                  戻る
                </button>
              </div>
            </div>
          </div>
        )}

        <section className={styles.section}>
          <div className={styles.summaryGrid}>
            {upcomingHostedCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  今後の主催イベント
                </div>
                <div className={styles.summaryValue}>{upcomingHostedCount}件</div>
              </div>
            )}
            {upcomingPerformingCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  今後の出演イベント
                </div>
                <div className={styles.summaryValue}>{upcomingPerformingCount}件</div>
              </div>
            )}
            {upcomingProvidingCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  今後の会場提供イベント
                </div>
                <div className={styles.summaryValue}>{upcomingProvidingCount}件</div>
              </div>
            )}
            {pendingTotal > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/image.svg" alt="" aria-hidden="true" />
                  <span>未承認ギャラリー</span>
                </div>
                <div className={styles.summaryValue}>{pendingTotal}件</div>
              </div>
            )}
          </div>
        </section>

        {/* 下書きエリア */}
        {draftEvents.length > 0 && (
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>下書き</h2>
            </div>
            <div className={styles.eventList}>
              {draftEvents.map((draft) => (
                <article key={draft.id} className={styles.eventCard}>
                  <div
                    className={styles.thumb}
                    style={{ background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}
                  >
                    下書き
                  </div>
                  <div>
                    <h3 className={styles.eventName}>{draft.name || '名称未設定'}</h3>
                    <p className={styles.eventDate}>
                      最終更新: {draft.updated_at ? new Date(draft.updated_at).toLocaleDateString('ja-JP') : '—'}
                    </p>
                    <div className={styles.actions}>
                      <Link
                        className={`${styles.btn} ${styles.editBtn}`}
                        to={`/events/create?draft=${draft.id}`}
                      >
                        再開
                      </Link>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnDanger}`}
                        onClick={() => handleDeleteDraft(draft)}
                      >
                        削除
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>イベント管理</h2>
            {canCreateEvent ? (
              <Link className={styles.createButton} to="/events/create">＋ 新規イベント作成</Link>
            ) : (
              <div className={styles.createHintBlock}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={() => setShowOnboardingIntro(true)}
                  disabled={sellerActionLoading}
                >
                  {sellerActionLoading ? '処理中...' : sellerStatus === 'none' ? '販売を開始する' : '続きへ'}
                </button>
              </div>
            )}
          </div>
          {relationSections.map((section) => (
            <div key={section.key} className={styles.relationBlock}>
              <h3 className={styles.relationTitle}>{section.title}</h3>
              {(() => {
                const canceledEvents = section.events.filter((event) => isCancelledStatus(event?.cancellation_status));
                const activeEvents = section.events.filter((event) => !isCancelledStatus(event?.cancellation_status));
                const upcomingEvents = activeEvents.filter((event) => !event.isPast);
                const endedEvents = activeEvents.filter((event) => event.isPast);

                return (
                  <>
                    {upcomingEvents.length > 0 ? (
                      <div className={styles.eventList}>
                        {upcomingEvents.map((event) => renderEventCard(event, section.relationType))}
                      </div>
                    ) : (
                      <p className={styles.emptyText}>開催前のイベントはありません。</p>
                    )}

                    {endedEvents.length > 0 && (
                      <details className={styles.fold}>
                        <summary>終了イベント（{endedEvents.length}件）</summary>
                        <div className={styles.eventList}>
                          {endedEvents.map((event) => renderEventCard(event, section.relationType))}
                        </div>
                      </details>
                    )}

                    {canceledEvents.length > 0 && (
                      <details className={styles.fold}>
                        <summary>中止イベント（{canceledEvents.length}件）</summary>
                        <div className={styles.eventList}>
                          {canceledEvents.map((event) => renderEventCard(event, section.relationType))}
                        </div>
                      </details>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
          {!relationSections.length && <p className={styles.emptyText}>表示できるイベントはありません。</p>}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>ギャラリー承認</h2>
            <Link className={styles.allLink} to="/gallery-admin">
              全イベントの承認ページへ
            </Link>
          </div>
          <div className={`${styles.gallerySection} ${pendingTotal > 0 ? styles.galleryAlert : ''}`}>
            {pendingTotal === 0 ? (
              <p className={styles.muted}>ギャラリーはありません。</p>
            ) : (
              <>
                <p>{pendingTotal}件の未承認画像があります。</p>
                {pendingEvents.map((item) => (
                  <div key={item.eventId}>
                    <Link className={styles.allLink} to={`/events/${item.eventPathId}/gallery-admin`}>
                      {item.eventName} の承認ページへ（{item.total}件）
                    </Link>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
